import { $createRangeSelection, $setSelection, LexicalEditor } from 'lexical';
import { isFunction } from 'lodash';
import { parseDropDataFromDragEvent } from 'modules/Lexical/helpers';
import { IMap } from 'typings/DeepIMap';
import { CombinedDocuments } from 'models/entities/CombinedDocument';
import { ReferenceCitation, ReferenceCitations } from 'models/entities/ReferenceCitation';
import { $copyStylesFromNeighbours } from '../../../helpers/copyStylesFromNeighbours';
import { $createReferenceCitationNode } from '../../../nodes/ReferenceCitationNode';

function getRange(event: DragEvent): Range | null {
  const x = event.clientX;
  const y = event.clientY;

  if (isFunction(document.caretPositionFromPoint)) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    const caret = document.caretPositionFromPoint(x, y);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (caret?.offsetNode) {
      const range = document.createRange();
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      range.setStart(caret.offsetNode, caret.offset);
      range.collapse(true);

      return range;
    }
  }

  if (isFunction(document.caretRangeFromPoint)) { // Safari support
    // eslint-disable-next-line
    return document.caretRangeFromPoint(x, y);
  }

  return null;
}

const changeReferenceIdIfAssetIdIsProvided = (
  references: IMap<ReferenceCitations>,
  storyCardDocuments: IMap<CombinedDocuments>,
  referenceId: string,
): string => {
  const assetNumber = (Object.values(storyCardDocuments.toJS()) as unknown as ReferenceCitation[])
    .find(item => item.id === referenceId)?.number;

  return (Object.values(references.toJS()) as unknown as ReferenceCitation[])
    .find(item => item.number === assetNumber)?.id ?? referenceId;
};

export function createDropHandler(
  editor: LexicalEditor,
  references: IMap<ReferenceCitations>,
  storyCardDocuments: IMap<CombinedDocuments>,
  saveHandler: () => void,
) {
  return (event: DragEvent): boolean => {
    event.preventDefault();
    const parsedData = parseDropDataFromDragEvent<{ referenceId: string }>(event);
    const referenceId = parsedData?.referenceId;
    if (!referenceId) {
      return false;
    }

    editor.update(() => {
      const range = getRange(event);
      if (!range) {
        return;
      }
      $setSelection(null);
      const selection = $createRangeSelection();
      selection.applyDOMRange(range);

      const referenceCitationNode = $createReferenceCitationNode(
        changeReferenceIdIfAssetIdIsProvided(references, storyCardDocuments, referenceId),
      );
      selection.insertNodes([referenceCitationNode]);
      $copyStylesFromNeighbours(referenceCitationNode);
    }, { discrete: true });

    saveHandler();

    return true;
  };
}
